import { useMemo } from 'react';
import { AuthUserType } from '../auth/types';
import { Role } from '../auth/types/roles';

/**
 * Derives which nav item titles the current user is allowed to see.
 * Must stay in sync with the `roles` annotations in config-navigation.tsx.
 *
 * Returns null when the user should see everything (admin / unknown role).
 */
export const useAllowedNavItems = (user?: AuthUserType) => {
  return useMemo(() => {
    const roleId = user?.roleId as Role | undefined;

    switch (roleId) {
      case Role.PATIENT:
        return ['patient', 'dispense', 'till'];

      case Role.RECEPTIONIST:
      case Role.ASSISTANT:
        return [
          'diary',
          'patient',
          'dispense',
          'till',
          'product',
          'recalls',
          'nhs claims',
        ];

      case Role.CLINICIAN:
      case Role.PROFESSIONAL:
        return ['diary', 'patient', 'reporting', 'nhs claims'];

      case Role.MANAGER:
      case Role.ACCOUNTANT:
        // managers see everything
        return null;

      case Role.MARKETING:
        return ['patient', 'recalls'];

      case Role.ADMIN:
      case Role.SYS_MANAGER:
      default:
        return null; // all items visible
    }
  }, [user?.roleId]);
};

/**
 * Derives which URL root segments the current user may navigate to.
 * Used by route guards to block direct URL access to forbidden pages.
 * Must stay in sync with useAllowedNavItems above.
 *
 * Returns null when all routes are allowed (admin / unknown role).
 */
export const useAllowedRouteRoots = (user?: AuthUserType) => {
  return useMemo(() => {
    const roleId = user?.roleId as Role | undefined;

    switch (roleId) {
      case Role.PATIENT:
        return ['patients', 'dispense', 'till', 'settings'];

      case Role.RECEPTIONIST:
      case Role.ASSISTANT:
        return [
          'diary',
          'patients',
          'dispense',
          'till',
          'product',
          'recalls',
          'claims',
          'settings',
        ];

      case Role.CLINICIAN:
      case Role.PROFESSIONAL:
        return ['diary', 'patients', 'reporting', 'claims', 'settings'];

      case Role.MANAGER:
      case Role.ACCOUNTANT:
        // managers can access every route
        return null;

      case Role.MARKETING:
        return ['patients', 'recalls', 'settings'];

      case Role.ADMIN:
      case Role.SYS_MANAGER:
      default:
        return null; // all routes allowed
    }
  }, [user?.roleId]);
};
